import type { ReactNode } from 'react';

import { Switch, cn } from 'shared-ui';

interface ToggleRowProps {
    label: string;
    checked: boolean;
    onCheckedChange: (v: boolean) => void;
    disabled?: boolean;
    /** Optional decorative element rendered before the label (e.g. a marker icon). */
    icon?: ReactNode;
    /** Extra classes for the label text (e.g. a heading-strength colour for rows without a section title). */
    labelClassName?: string;
    /** Extra classes for the row itself (e.g. dropping the inset so a standalone row aligns with section titles). */
    className?: string;
}

/**
 * Single-row label + switch toggle used by MapLayersPanel, FiltersPanel, and StatsConfigPanel.
 * The optional `disabled` prop locks the switch without hiding it (used to enforce a minimum
 * of one visible stat in the stats config panel).
 */
export function ToggleRow({
    label,
    checked,
    onCheckedChange,
    disabled,
    icon,
    labelClassName,
    className,
}: ToggleRowProps) {
    return (
        <div className={cn('flex items-center justify-between gap-4 py-1.5 pl-2', className)}>
            <span className="flex min-w-0 items-center gap-2">
                {icon}
                <span
                    className={cn(
                        'text-muted-foreground min-w-0 truncate text-sm leading-5 font-normal tracking-normal',
                        labelClassName,
                    )}
                >
                    {label}
                </span>
            </span>
            <Switch
                size="default"
                checked={checked}
                onCheckedChange={onCheckedChange}
                disabled={disabled}
                aria-label={label}
            />
        </div>
    );
}
